import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Artifact, ConversationStatus } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Row from '@amzn/meridian/row';

import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useTitle } from 'src/hooks';

import './AI.scss';
import { AIChatArtifact } from './AIChatArtifact';
import { AIChatList } from './AIChatList';
import { AIChatMainArea } from './AIChatMainArea';
import { ChatState, Conversation, Message } from './types';
import {
    getArtifacts,
    getConversation,
    getConversationStatus,
    listConversations,
    pollForStatusAndMessages,
    sendMessage,
    sortConversationsByTimestamp
} from './utils';
import { getMaxTimestamp } from './utils';

export const AI = () => {
    const { conversationId, timestamp } = useParams<{ conversationId?: string; timestamp?: string }>();
    const history = useHistory();
    const { userInfo, setTopAlertMsg, setIsSideBarClosed } = useContext(AppContext);

    const [chatState, setChatState] = useState<ChatState>({
        currentConversationId: conversationId || null,
        conversations: [],
        messages: [],
        isLoadingConversations: false,
        isLoadingMessages: false,
        isLoadingArtifacts: false,
        error: null
    });

    const [isAIResponding, setIsAIResponding] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<ConversationStatus>(ConversationStatus.READY);
    const [stopPolling, setStopPolling] = useState<(() => void) | null>(null);

    // Artifact view mode state
    const isArtifactViewMode = !!(conversationId && timestamp);
    const [sharedArtifacts, setSharedArtifacts] = useState<Artifact[]>([]);
    const [isLoadingSharedArtifacts, setIsLoadingSharedArtifacts] = useState(false);

    // Track the active conversation ID synchronously
    // This ref is updated immediately when URL changes
    const activeConversationRef = React.useRef<string | null>(conversationId || null);

    // AI Chat conversation caching
    const [cachedConversations, setCachedConversations] = useState<Conversation[]>([]);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [hasLoadedConversations, setHasLoadedConversations] = useState(false);

    useTitle('AI Chat');

    const isNewChat = !conversationId;

    useEffect(() => {
        setIsSideBarClosed(true);
    }, []);

    // Load conversations on mount
    useEffect(() => {
        if (userInfo.username && !hasLoadedConversations && !isLoadingConversations) {
            loadConversations();
        }
    }, [userInfo.username, hasLoadedConversations, isLoadingConversations]);

    const loadConversations = async () => {
        if (hasLoadedConversations || isLoadingConversations) {
            return;
        }

        setIsLoadingConversations(true);

        try {
            const conversations = await listConversations();
            const sortedConversations = sortConversationsByTimestamp(conversations);
            setCachedConversations(sortedConversations);
            setHasLoadedConversations(true);
        } catch (error) {
            console.error('Error loading conversations:', error);
            setTopAlertMsg(error);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    const handleRefreshConversations = async () => {
        setHasLoadedConversations(false);
        setIsLoadingConversations(true);

        try {
            const conversations = await listConversations();
            const sortedConversations = sortConversationsByTimestamp(conversations);
            setCachedConversations(sortedConversations);
            setHasLoadedConversations(true);
        } catch (error) {
            console.error('Error refreshing conversations:', error);
            setTopAlertMsg(error);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    const handleRefreshConversationsQuietly = async () => {
        try {
            const conversations = await listConversations();
            const sortedConversations = sortConversationsByTimestamp(conversations);
            setCachedConversations(sortedConversations);
        } catch (error) {
            console.error('Error refreshing conversations:', error);
        }
    };

    // Load conversation messages when conversationId changes
    useEffect(() => {
        // Update active conversation ref immediately (synchronous)
        // This ensures polling callbacks can detect
        activeConversationRef.current = conversationId || null;

        // Reset AI responding state immediately when switching conversations
        setIsAIResponding(false);
        setCurrentStatus(ConversationStatus.READY);

        if (conversationId) {
            // Update currentConversationId immediately before loading
            setChatState((prev) => ({
                ...prev,
                currentConversationId: conversationId
            }));

            if (isArtifactViewMode && timestamp) {
                // Load artifacts instead of full conversation
                loadArtifacts(conversationId, timestamp);
            } else {
                // Load full conversation
                loadConversation(conversationId);
            }
        } else {
            // Clear messages for new chat and reset status
            setChatState((prev) => ({
                ...prev,
                currentConversationId: null,
                messages: [],
                error: null
            }));
            // Stop any existing polling
            if (stopPolling) {
                stopPolling();
                setStopPolling(null);
            }
        }
    }, [conversationId, timestamp, isArtifactViewMode]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (stopPolling) {
                stopPolling();
            }
        };
    }, [stopPolling]);

    const startPolling = (
        pollingConversationId: string,
        lastTimestamp: string | number,
        optimisticMessage?: Message
    ) => {
        const stopPollingFn = pollForStatusAndMessages(
            pollingConversationId,
            lastTimestamp,
            // onNewMessages - add new messages as they come in
            (newMessages: Message[]) => {
                // pollingConversationId is captured in closure when startPolling was called
                // activeConversationRef.current is the live current conversation ID
                if (pollingConversationId !== activeConversationRef.current) {
                    return;
                }

                setChatState((prev) => {
                    if (optimisticMessage) {
                        // Check if we have a user message that matches our optimistic message
                        const serverUserMessage = newMessages.find(
                            (msg) =>
                                msg.messageType === 'user' && msg.messageContent === optimisticMessage.messageContent
                        );

                        let updatedMessages = [...prev.messages];

                        if (serverUserMessage) {
                            // Replace the optimistic message with the real one from server
                            const optimisticIndex = updatedMessages.findIndex((msg) => msg === optimisticMessage);
                            if (optimisticIndex !== -1) {
                                updatedMessages[optimisticIndex] = serverUserMessage;
                            }

                            // Add only the non-user messages (tool/system messages)
                            const nonUserMessages = newMessages.filter((msg) => msg !== serverUserMessage);
                            updatedMessages = [...updatedMessages, ...nonUserMessages];
                        } else {
                            // No matching user message, just add all new messages
                            updatedMessages = [...updatedMessages, ...newMessages];
                        }

                        return {
                            ...prev,
                            messages: updatedMessages
                        };
                    } else {
                        // No optimistic message, just add all new messages
                        return {
                            ...prev,
                            messages: [...prev.messages, ...newMessages]
                        };
                    }
                });
            },
            // onStatusChange - update status and AI responding state
            (status: ConversationStatus) => {
                if (pollingConversationId !== activeConversationRef.current) {
                    return;
                }

                setCurrentStatus(status);
                setIsAIResponding(status === ConversationStatus.WORKING);
            },
            // onComplete - stop polling when ready
            () => {
                if (pollingConversationId !== activeConversationRef.current) {
                    return;
                }

                setIsAIResponding(false);
                setCurrentStatus(ConversationStatus.READY);
                setStopPolling(null);
            },
            // onError - handle polling errors
            (error: Error) => {
                if (pollingConversationId !== activeConversationRef.current) {
                    return;
                }

                console.error('Polling error:', error);
                setIsAIResponding(false);
                setCurrentStatus(ConversationStatus.READY);
                setStopPolling(null);
                setChatState((prev) => ({
                    ...prev,
                    error: error.message
                }));
            }
        );

        setStopPolling(() => stopPollingFn);
    };

    const loadArtifacts = async (conversationId: string, artifactTimestamp: string) => {
        setIsLoadingSharedArtifacts(true);
        setChatState((prev) => ({ ...prev, error: null }));

        try {
            const artifacts = await getArtifacts(conversationId, artifactTimestamp);
            setSharedArtifacts(artifacts);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load artifacts';
            setChatState((prev) => ({
                ...prev,
                error: errorMessage
            }));
            console.error('Error loading artifacts:', error);
            setTopAlertMsg(error);
        } finally {
            setIsLoadingSharedArtifacts(false);
        }
    };

    const loadConversation = async (convId: string) => {
        setChatState((prev) => ({ ...prev, isLoadingMessages: true, error: null }));

        try {
            const messages = await getConversation(convId);
            setChatState((prev) => ({
                ...prev,
                messages,
                isLoadingMessages: false
            }));

            // Check conversation status and handle accordingly
            try {
                const status = await getConversationStatus(convId);
                setCurrentStatus(status);

                if (status === ConversationStatus.WORKING) {
                    setIsAIResponding(true);
                    // Start polling from the last message timestamp
                    if (messages.length > 0) {
                        const lastTimestamp = getMaxTimestamp(messages.map((msg) => msg.timestamp));
                        startPolling(convId, lastTimestamp);
                    }
                } else if (status === ConversationStatus.CLOSED || status === ConversationStatus.ERROR) {
                    // Conversation is closed or errored, disable further interaction
                    setIsAIResponding(false);
                    // Don't start polling for closed/errored conversations
                } else if (status === ConversationStatus.READY) {
                    // Conversation is ready for new messages
                    setIsAIResponding(false);
                }
            } catch (statusError) {
                console.error('Error checking conversation status:', statusError);
                setTopAlertMsg(statusError);
                // Don't fail the whole load if status check fails
                // Default to READY state if we can't get status
                setCurrentStatus(ConversationStatus.READY);
                setIsAIResponding(false);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load conversation';
            setChatState((prev) => ({
                ...prev,
                error: errorMessage,
                isLoadingMessages: false
            }));
            console.error('Error loading conversation:', error);
            setTopAlertMsg(error);
        }
    };

    const handleSendMessage = useCallback(
        async (message: string, endpoint: string, toolkits: string[] | undefined) => {
            // Stop any existing polling
            if (stopPolling) {
                stopPolling();
                setStopPolling(null);
            }

            // Optimistic UI update - add user message immediately
            const optimisticMessage: Message = {
                messageContent: message,
                messageType: 'user',
                timestamp: (Date.now() * 1000000).toString(), // Convert to nanoseconds as string
                toolUsage: undefined
            };

            setChatState((prev) => ({
                ...prev,
                messages: [...prev.messages, optimisticMessage],
                error: null
            }));

            try {
                // Send the message
                const newConversationId = await sendMessage(
                    chatState.currentConversationId,
                    userInfo.username,
                    message,
                    endpoint,
                    toolkits
                );

                // Start polling for status and messages
                const conversationIdToUse = chatState.currentConversationId || newConversationId;
                if (conversationIdToUse) {
                    const lastMessageTimestamp = getMaxTimestamp([
                        ...chatState.messages.map((msg) => msg.timestamp),
                        optimisticMessage.timestamp
                    ]);

                    startPolling(conversationIdToUse, lastMessageTimestamp, optimisticMessage);
                }

                // If this was a new chat, update the URL with the conversation ID
                // Do this AFTER starting polling so polling continues
                if (!chatState.currentConversationId && newConversationId) {
                    setChatState((prev) => ({
                        ...prev,
                        currentConversationId: newConversationId
                    }));
                    history.push(`${ROUTES.AI.INDEX}/${newConversationId}`);
                    // Refresh conversation list quietly
                    handleRefreshConversationsQuietly();
                }
            } catch (error) {
                // Remove the optimistic message on error
                setChatState((prev) => ({
                    ...prev,
                    messages: prev.messages.filter((msg) => msg !== optimisticMessage),
                    error: error instanceof Error ? error.message : 'Failed to send message'
                }));
                console.error('Error sending message:', error);
                setTopAlertMsg(error);
            }
        },
        [chatState, userInfo.username, stopPolling]
    );

    // Render artifacts view if in artifact mode
    if (isArtifactViewMode && conversationId && timestamp) {
        return (
            <AIChatArtifact
                conversationId={conversationId}
                timestamp={timestamp}
                artifacts={sharedArtifacts}
                isLoading={isLoadingSharedArtifacts}
                error={chatState.error}
                isSharedView={true}
            />
        );
    }

    // Render normal chat view with sidebar
    return (
        <Row widths={[325, 'fill']} width="100%" height="100%">
            <AIChatList
                cachedConversations={cachedConversations}
                isLoadingConversations={isLoadingConversations}
                currentConversationId={chatState.currentConversationId}
                onRefresh={handleRefreshConversations}
            />
            <AIChatMainArea
                conversationId={chatState.currentConversationId}
                messages={chatState.messages}
                onSendMessage={handleSendMessage}
                isLoading={chatState.isLoadingMessages}
                isNewChat={isNewChat}
                isAIResponding={isAIResponding}
                isDisabled={
                    currentStatus === ConversationStatus.WORKING ||
                    currentStatus === ConversationStatus.CLOSED ||
                    currentStatus === ConversationStatus.ERROR
                }
                conversationStatus={currentStatus}
            />
        </Row>
    );
};
